import React, { useEffect, useState } from 'react';
import { Loader2, Flag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { OccurredAtField } from './OccurredAtField';
import { CONTAINER_OPERATIONS } from './operations';
import { bookingsApi } from '../../services/api';
import { formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';

// Bridge dialog: record a lifecycle milestone for a booking, optionally dated in the past.
export function RecordMilestoneDialog({ bookingId, bookingNumber, open, onOpenChange, onSaved }) {
  const [operation, setOperation] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(function () {
    if (open) { setOperation(''); setLocation(''); setNotes(''); setOccurredAt(''); }
  }, [open]);

  async function save(e) {
    e.preventDefault();
    if (!operation) { toast.error('Select the milestone to record'); return; }
    setSaving(true);
    try {
      const res = await bookingsApi.containerOperation(bookingId, { operation_type: operation, location: location.trim() || null, notes: notes.trim() || null, occurred_at: occurredAt || null });
      toast.success(res.data.message + ' — ' + formatDateTime(res.data.event.timestamp));
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the milestone');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="record-milestone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center"><Flag className="h-5 w-5 text-blue-700" strokeWidth={1.75} /></div>
            Record milestone <span className="sv-mono text-gray-600 font-normal">{bookingNumber}</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="space-y-1.5">
            <Label>Milestone</Label>
            <select value={operation} onChange={function (e) { setOperation(e.target.value); }} className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-sm" data-testid="milestone-operation">
              <option value="">Select a milestone…</option>
              {Object.entries(CONTAINER_OPERATIONS).map(function (entry) { return <option key={entry[0]} value={entry[0]}>{entry[1].label} — {entry[1].description}</option>; })}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Location</Label><Input value={location} onChange={function (e) { setLocation(e.target.value); }} placeholder="e.g. Singapore, Singapore" className="rounded-sm" data-testid="milestone-location" /></div>
            <div className="space-y-1.5"><Label>Notes (shown to the customer)</Label><Input value={notes} onChange={function (e) { setNotes(e.target.value); }} placeholder="Optional" className="rounded-sm" data-testid="milestone-notes" /></div>
          </div>
          <OccurredAtField value={occurredAt} onChange={setOccurredAt} testId="milestone-occurred" />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={function () { onOpenChange(false); }} data-testid="milestone-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="milestone-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record milestone'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
